import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaCloudUploadAlt, FaCheckCircle } from 'react-icons/fa';
import { uploadEventPhotos } from '../services/api';
import { compressImageForUpload } from '../utils/imageCompression';

const EVENT_PHOTO_MAX_SOURCE_BYTES = 30 * 1024 * 1024;
const EVENT_PHOTO_UPLOAD_BYTES = 1800 * 1024;
const EVENT_PHOTO_MAX_DIMENSION = 2400;

const EventPhotos = () => {
  const { eventId } = useParams();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate files
    const validFiles = selectedFiles.filter(file => {
      if (!file.type.startsWith('image/')) {
        setError('Seuls les fichiers image sont autorisés');
        return false;
      }
      if (file.size > EVENT_PHOTO_MAX_SOURCE_BYTES) {
        setError('Chaque fichier doit faire moins de 30 Mo');
        return false;
      }
      return true;
    });

    setFiles(validFiles);
    setError('');
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Veuillez sélectionner au moins une photo');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      const uploadedPhotos = [];

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        setUploadProgress(Math.round((index / files.length) * 100));

        const preparedFile = await compressImageForUpload(
          file,
          EVENT_PHOTO_UPLOAD_BYTES,
          EVENT_PHOTO_MAX_DIMENSION,
          'Event photo'
        );

        const formData = new FormData();
        formData.append('photos[]', preparedFile);
        formData.append('original_names[]', file.name);

        const response = await uploadEventPhotos(eventId, formData);
        uploadedPhotos.push(...(response.photos || []));
        setUploadProgress(Math.round(((index + 1) / files.length) * 100));
      }

      const totalFaces = uploadedPhotos.reduce((sum, photo) => sum + (photo.faces_detected || 0), 0);

      setSuccess(
        totalFaces > 0
          ? `${uploadedPhotos.length} photos importées avec succès ! ${totalFaces} visages détectés et associés aux utilisateurs.`
          : `${uploadedPhotos.length} photos importées avec succès.`
      );
      setFiles([]);
      setUploadProgress(100);

      setTimeout(() => {
        navigate(`/event/${eventId}/browse`);
      }, 3000);
    } catch (err) {
      setError(err.message || "L'import a échoué. Veuillez réessayer avec des photos plus petites.");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  return (
    <div className="container">
      <div className="upload-container" style={{ maxWidth: '800px' }}>
        <h2 style={{ textAlign: 'center', color: 'var(--primary-orange)', marginBottom: '1rem' }}>
          Importer les photos de l'événement
        </h2>
        <p style={{ textAlign: 'center', color: 'var(--gray)', marginBottom: '2rem' }}>
          Importez les photos de votre événement. Notre IA détectera automatiquement les visages et les associera aux utilisateurs inscrits.
        </p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div 
          className="upload-zone"
          onClick={() => document.getElementById('multiFileInput').click()}
        >
          <div className="upload-icon">
            <FaCloudUploadAlt />
          </div>
          <h3>Cliquez pour sélectionner des photos</h3>
          <p style={{ marginTop: '0.5rem' }}>Vous pouvez sélectionner plusieurs photos à la fois</p>
          <p style={{ fontSize: '0.9rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
            Formats acceptés : JPG, PNG, WEBP (source max. 30 Mo, optimisée avant import)
          </p>
        </div>

        <input
          id="multiFileInput"
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {files.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>
              Photos sélectionnées ({files.length})
            </h3>
            
            <div style={{ 
              maxHeight: '300px', 
              overflowY: 'auto', 
              background: 'var(--off-white)', 
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              {files.map((file, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.8rem',
                    background: 'var(--white)',
                    marginBottom: '0.5rem',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px var(--shadow)'
                  }}
                >
                  <div>
                    <strong>{file.name}</strong>
                    <span style={{ color: 'var(--gray)', marginLeft: '1rem' }}>
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    style={{
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Retirer
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={handleUpload}
              className="btn-submit"
              disabled={uploading}
            >
              {uploading 
                ? `Import et traitement... ${uploadProgress}%`
                : `Importer ${files.length} photo${files.length > 1 ? 's' : ''}`}
            </button>
          </div>
        )}

        <div style={{ 
          marginTop: '2rem', 
          padding: '1.5rem', 
          background: 'var(--off-white)', 
          borderRadius: '12px',
          border: '2px solid var(--light-green)'
        }}>
          <h4 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>
            <FaCheckCircle /> Que se passe-t-il ensuite ?
          </h4>
          <ul style={{ color: 'var(--gray)', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
            <li>Les photos sont importées dans l'événement</li>
            <li>L'IA détecte automatiquement tous les visages de chaque photo</li>
            <li>Les visages sont comparés aux profils utilisateurs inscrits</li>
            <li>Les utilisateurs sont informés de leurs photos trouvées</li>
            <li>Des scores de confiance sont calculés pour chaque correspondance</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default EventPhotos;
